import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import useAuth from "../../hooks/useAuth";
import useAccountActions from "../../hooks/useAccountActions";
import useAppModernisation from "../../hooks/useAppModernisation";
import { usePortal } from "../../hooks/usePortal";
import LoginPage from "./LoginPage";
import LoginFailurePopup from "./LoginFailurePopup";
import LoginFailureClosePopup from "./LoginFailureClosePopup";
import { allowLogin, getReferrer } from "../../utils/authUtils";

const CACHE_SUBSCRIBED_KEY = "subscribedAppsAccess";

const APP_URL_MAP = {
  tg: import.meta.env.VITE_TG_APP_BASE_URL,
  crush: import.meta.env.VITE_DRTC_APP_BASE_URL,
  obesity: import.meta.env.VITE_OBESID_APP_BASE_URL,
};

const ROUTE_DECISIONS = {
  LOADING: "LOADING",
  LOGIN: "LOGIN",
  POPUP_BLOCK: "POPUP_BLOCK",
  REDIRECT: "REDIRECT",
  ALLOW: "ALLOW",
};

const getAppRedirectUrl = (app) => {
  const url = new URL(APP_URL_MAP[app]);
  url.searchParams.set("targetApp", app);
  const referrer = getReferrer();
  if (referrer) {
    url.searchParams.set("r", referrer);
  }
  return url.toString();
};

const getPortalLoginUrl = (portalUrl, currentApp) => {
  const url = new URL(portalUrl);
  url.searchParams.set("appReturnUrl", window.location.href);

  if (currentApp) {
    url.searchParams.set("targetApp", currentApp);
  }

  return url.toString();
};

const getCachedAccess = () => {
  try {
    const raw = localStorage.getItem(CACHE_SUBSCRIBED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const canBypassProtectedRoute = (currentApp) => {
  const cachedAccess = getCachedAccess();

  if (!cachedAccess?.hasCurrentAppAccess) {
    return false;
  }

  if (
    Array.isArray(cachedAccess.appSlugs) &&
    cachedAccess.appSlugs.length > 0 &&
    !cachedAccess.appSlugs.includes(currentApp)
  ) {
    return false;
  }

  if (cachedAccess.expiresAt) {
    const expiry = new Date(cachedAccess.expiresAt).getTime();
    if (!allowLogin(new Date(expiry))) {
      return false;
    }
  }

  return true;
};

const getProtectedRouteDecision = ({
  isLoading,
  silentLoginAttempted,
  b2cDisabled,
  isLogged,
  isInstitution,
  email,
  subscriptionDetails,
  subscriptionLoading,
  subscriptionCheckAttempted,
  hasAnySubscription,
  hasAccessToCurrentApp,
  isMoibileOrTabletApp,
  appSlugs,
  isAppResolved,
  currentApp,
}) => {
  const noSubscriptionTitle =
    subscriptionDetails?.error === "Subscription activation in progress"
      ? "Subscription activation in progress"
      : "Please check that you have completed the subscription process";
  const noSubscriptionMessage =
    subscriptionDetails?.error === "Subscription activation in progress"
      ? "Subscription activation in progress"
      : subscriptionDetails?.error || "Sorry, we can't find your subscription details.";
  const institutionNoSubscriptionTitle = "Please sign in to continue";
  const institutionNoSubscriptionMessage =
    "We couldn't verify an active subscription for this account. Please sign in with a different account or contact support.";
  const noAppAccessTitle = "You don't have access to this app";
  const noAppAccessMessage =
    "Your account doesn't have access to this app. Please sign in with a different account or contact support.";
  const appShellBlockTitle = isInstitution ? institutionNoSubscriptionTitle : noAppAccessTitle;
  const appShellBlockMessage = isInstitution
    ? "We need you to sign in to verify your access to this app."
    : noAppAccessMessage;
  const shouldWaitForAlternateAuth =
    !b2cDisabled &&
    Boolean(email) &&
    hasAnySubscription &&
    !hasAccessToCurrentApp &&
    (subscriptionLoading || !subscriptionCheckAttempted);

  if (isLoading || !silentLoginAttempted || shouldWaitForAlternateAuth) {
    return { type: ROUTE_DECISIONS.LOADING };
  }

  if (
    isMoibileOrTabletApp &&
    !b2cDisabled &&
    isLogged &&
    hasAnySubscription &&
    !hasAccessToCurrentApp
  ) {
    return {
      type: ROUTE_DECISIONS.POPUP_BLOCK,
      loginMode: "LOGIN_ONLY",
      forceShowLogin: true,
      popupTitle: appShellBlockTitle,
      popupMessage: appShellBlockMessage,
    };
  }

  if (!b2cDisabled && isLogged && !isInstitution && !hasAnySubscription) {
    return {
      type: ROUTE_DECISIONS.POPUP_BLOCK,
      loginMode: "LOGIN_ONLY",
      forceShowLogin: true,
      popupTitle: noSubscriptionTitle,
      popupMessage: noSubscriptionMessage,
    };
  }

  if (!b2cDisabled && isLogged && isInstitution && !hasAnySubscription) {
    return {
      type: ROUTE_DECISIONS.POPUP_BLOCK,
      loginMode: "LOGIN_ONLY",
      forceShowLogin: true,
      popupTitle: institutionNoSubscriptionTitle,
      popupMessage: institutionNoSubscriptionMessage,
    };
  }

  if (!b2cDisabled && isLogged && isInstitution && hasAnySubscription && !hasAccessToCurrentApp) {
    return {
      type: ROUTE_DECISIONS.POPUP_BLOCK,
      loginMode: "LOGIN_ONLY",
      forceShowLogin: true,
      popupTitle: noAppAccessTitle,
      popupMessage: noAppAccessMessage,
    };
  }

  if (!b2cDisabled && isInstitution && !hasAnySubscription) {
    return {
      type: ROUTE_DECISIONS.LOGIN,
      loginMode: "LOGIN_ONLY",
      forceShowLogin: true,
    };
  }

  if (hasAccessToCurrentApp) {
    return { type: ROUTE_DECISIONS.ALLOW };
  }

  if (!b2cDisabled && !hasAnySubscription) {
    return {
      type: ROUTE_DECISIONS.LOGIN,
      loginMode: "LOGIN_ONLY",
    };
  }

  if (appSlugs.length === 1) {
    const app = appSlugs[0];

    if (app === currentApp) {
      return { type: ROUTE_DECISIONS.ALLOW };
    }

    if (isMoibileOrTabletApp) {
      return {
        type: ROUTE_DECISIONS.POPUP_BLOCK,
        loginMode: "LOGIN_ONLY",
        forceShowLogin: true,
        popupTitle: noAppAccessTitle,
        popupMessage: noAppAccessMessage,
      };
    }

    return {
      type: ROUTE_DECISIONS.REDIRECT,
      targetApp: app,
      redirectTo: getAppRedirectUrl(app),
    };
  }

  return { type: ROUTE_DECISIONS.ALLOW };
};

const OnlineProtectedRoute = ({ children }) => {
  const { accounts, inProgress } = useMsal();
  const { isMoibileOrTabletApp } = useAppModernisation();
  const {
    isLogged,
    isLoading,
    b2cDisabled,
    isInstitution,
    email,
    subscriptionLoading,
    subscriptionCheckAttempted,
    availableAppSlugs,
    subscriptionDetails,
    hasAnySubscription,
    silentLoginAttempted,
  } = useAuth();
  const { logout } = useAccountActions();
  const { isPortalEnabled, portalUrl } = usePortal();

  const [showBlockPopup, setShowBlockPopup] = useState(false);

  const appSlugs = availableAppSlugs;
  const resolvedTargetApp = sessionStorage.getItem("targetApp");
  const hasExplicitInstitutionLogout =
    sessionStorage.getItem("explicitLogout_institution") === "true";
  const currentApp = import.meta.env.VITE_BRAND || "tg";
  const isAppResolved = resolvedTargetApp === currentApp;
  const hasCurrentAppAccess = appSlugs.includes(currentApp);
  const hasMsalAccount = accounts.length > 0;
  const shouldDeferPortalRedirect = !b2cDisabled && (inProgress !== "none" || hasMsalAccount);

  const routeDecision = useMemo(
    () =>
      getProtectedRouteDecision({
        isLoading,
        silentLoginAttempted,
        b2cDisabled,
        isLogged,
        isInstitution,
        email,
        subscriptionDetails,
        subscriptionLoading,
        subscriptionCheckAttempted,
        hasAnySubscription,
        hasAccessToCurrentApp: hasCurrentAppAccess,
        isMoibileOrTabletApp,
        appSlugs,
        isAppResolved,
        currentApp,
      }),
    [
      isLoading,
      silentLoginAttempted,
      b2cDisabled,
      isLogged,
      isInstitution,
      email,
      subscriptionDetails,
      subscriptionLoading,
      subscriptionCheckAttempted,
      hasAnySubscription,
      hasCurrentAppAccess,
      isMoibileOrTabletApp,
      appSlugs,
      isAppResolved,
      currentApp,
    ],
  );

  const handleBackAndLogout = async () => {
    try {
      await logout({
        skipInstitutionPostLogoutRedirect:
          isInstitution && hasAnySubscription && !hasCurrentAppAccess,
      });
    } catch (err) {
      console.error("ProtectedRoute: logout failed", err);
      setShowBlockPopup(false);
    }
  };

  useEffect(() => {
    setShowBlockPopup(routeDecision.type === ROUTE_DECISIONS.POPUP_BLOCK);
  }, [routeDecision.type]);

  useEffect(() => {
    if (routeDecision.type === ROUTE_DECISIONS.REDIRECT) {
      window.location.replace(routeDecision.redirectTo);
    }
  }, [routeDecision]);

  if (routeDecision.type === ROUTE_DECISIONS.LOADING) {
    return <div className="loader"></div>;
  }

  if (hasExplicitInstitutionLogout) {
    return <LoginPage mode="LOGIN_ONLY" forceShowLogin />;
  }

  if (routeDecision.type === ROUTE_DECISIONS.LOGIN) {
    if (!isMoibileOrTabletApp && isPortalEnabled && !shouldDeferPortalRedirect) {
      window.location.replace(getPortalLoginUrl(portalUrl, currentApp));
      return null;
    }

    if (!isMoibileOrTabletApp && isPortalEnabled && shouldDeferPortalRedirect) {
      return <div className="loader"></div>;
    }

    return (
      <LoginPage mode={routeDecision.loginMode} forceShowLogin={routeDecision.forceShowLogin} />
    );
  }

  if (routeDecision.type === ROUTE_DECISIONS.POPUP_BLOCK) {
    return (
      <>
        <LoginPage mode={routeDecision.loginMode} forceShowLogin={routeDecision.forceShowLogin} />
        {subscriptionDetails?.error === "Subscription activation in progress" && (
          <LoginFailureClosePopup
            show={showBlockPopup}
            title={routeDecision.popupTitle}
            onClose={handleBackAndLogout}
          />
        )}
        {subscriptionDetails?.error !== "Subscription activation in progress" && (
          <LoginFailurePopup
            show={showBlockPopup}
            title={routeDecision.popupTitle}
            message={routeDecision.popupMessage}
            onClose={handleBackAndLogout}
          />
        )}
      </>
    );
  }

  if (routeDecision.type === ROUTE_DECISIONS.REDIRECT) {
    return <div className="loader"></div>;
  }

  return children;
};

OnlineProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const ProtectedRoute = ({ children }) => {
  const currentApp = import.meta.env.VITE_BRAND || "tg";

  if (canBypassProtectedRoute(currentApp)) {
    return children;
  }

  return <OnlineProtectedRoute>{children}</OnlineProtectedRoute>;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
